// sections/@dashboard/finance/ExpensesPanel.tsx
// ─── BRAND NEW ────────────────────────────────────────────────────────────────
// Full expense management:
//  - List all expenses with category, amount, date, description
//  - Add expense dialog
//  - Delete expense
//  - Summary totals by category
//  - Monthly filter

import { useEffect, useState } from "react";
import {
  Stack,
  Grid,
  Card,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Alert,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSnackbar } from "notistack";
import { FinanceService } from "../../api/finance.service";
import { SelectedPeriod } from "../../pages/dashboard/finance/FinancePeriodSelector";
import Iconify from "../iconify";
import { fCurrency } from "../../utils/formatNumber";
import Scrollbar from "../scrollbar/Scrollbar";
import { fDate } from "../../utils/formatTime";

// ── Types ──────────────────────────────────────────────────────────────────────
interface IExpense {
  id: number;
  date: string;
  description: string;
  category: string;
  amount: number;
  vatCode?: "S" | "E" | "Z" | "N";
  reference?: string;
  createdAt: string;
}

// ── Expense categories ─────────────────────────────────────────────────────────
const EXPENSE_CATEGORIES = [
  { value: "salaries", label: "Salaries & Wages", icon: "eva:people-fill" },
  { value: "rent", label: "Rent & Property", icon: "eva:home-fill" },
  { value: "utilities", label: "Utilities", icon: "eva:flash-fill" },
  {
    value: "repairs",
    label: "Repairs & Maintenance",
    icon: "eva:settings-2-fill",
  },
  { value: "insurance", label: "Insurance", icon: "eva:shield-fill" },
  {
    value: "marketing",
    label: "Marketing & Advertising",
    icon: "eva:trending-up-fill",
  },
  { value: "it", label: "IT & Software", icon: "eva:monitor-fill" },
  {
    value: "equipment",
    label: "Equipment & Clinical",
    icon: "eva:briefcase-fill",
  },
  {
    value: "professional",
    label: "Professional Services",
    icon: "eva:file-text-fill",
  },
  {
    value: "sundries",
    label: "Sundries & Consumables",
    icon: "eva:shopping-bag-fill",
  },
  { value: "other", label: "Other", icon: "eva:more-horizontal-fill" },
];

const VAT_OPTIONS = [
  { value: "S", label: "S — Standard 20%" },
  { value: "E", label: "E — Exempt" },
  { value: "Z", label: "Z — Zero-rated" },
  { value: "N", label: "N — No VAT" },
];

// ── Add Expense Dialog ─────────────────────────────────────────────────────────
function AddExpenseDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: null as Date | null,
    description: "",
    category: "",
    amount: "",
    vatCode: "S",
    reference: "",
  });

  const reset = () =>
    setForm({
      date: null,
      description: "",
      category: "",
      amount: "",
      vatCode: "S",
      reference: "",
    });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (!form.date || !form.description || !form.category || !form.amount)
      return;
    try {
      setSaving(true);
      await new FinanceService().createExpense({
        date: form.date.toISOString().split("T")[0],
        description: form.description,
        category: form.category,
        amount: parseFloat(form.amount),
        vatCode: form.vatCode as any,
        reference: form.reference || undefined,
      });
      enqueueSnackbar("Expense recorded successfully!");
      reset();
      onSaved();
      onClose();
    } catch {
      enqueueSnackbar("Failed to save expense", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const isValid =
    form.date &&
    form.description.trim() &&
    form.category &&
    parseFloat(form.amount) > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Expense</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={form.date}
              onChange={(v) => setForm((f) => ({ ...f, date: v }))}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </LocalizationProvider>

          <TextField
            size="small"
            fullWidth
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm((f) => ({ ...f, description: e.target.value }))
            }
            placeholder="e.g. Monthly rent — 703 Ormskirk Road"
          />

          <TextField
            select
            size="small"
            fullWidth
            label="Category"
            value={form.category}
            onChange={(e) =>
              setForm((f) => ({ ...f, category: e.target.value }))
            }>
            {EXPENSE_CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Iconify
                    icon={c.icon}
                    width={16}
                    sx={{ color: "text.secondary" }}
                  />
                  <span>{c.label}</span>
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(e) =>
                setForm((f) => ({ ...f, amount: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              size="small"
              label="VAT code"
              value={form.vatCode}
              onChange={(e) =>
                setForm((f) => ({ ...f, vatCode: e.target.value }))
              }
              sx={{ flex: 1 }}>
              {VAT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            size="small"
            fullWidth
            label="Reference / invoice number (optional)"
            value={form.reference}
            onChange={(e) =>
              setForm((f) => ({ ...f, reference: e.target.value }))
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || saving}
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : undefined
          }>
          {saving ? "Saving…" : "Record Expense"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────
interface ExpensesPanelProps {
  selectedPeriod: SelectedPeriod | null;
}

export default function ExpensesPanel({ selectedPeriod }: ExpensesPanelProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [expenses, setExpenses] = useState<IExpense[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [filterMonth, setFilterMonth] = useState<Date | null>(null);
  const [filterCategory, setFilterCategory] = useState("all");

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const loadExpenses = async () => {
    try {
      setLoading(true);
      const params: any = {};
      // Scope to the accounting period selected at the page level, so the tab
      // never shows entries from outside the period being worked on.
      if (selectedPeriod) {
        params.dateFrom = selectedPeriod.dateFrom;
        params.dateTo = selectedPeriod.dateTo;
      }
      if (filterMonth) {
        const y = filterMonth.getFullYear();
        const m = String(filterMonth.getMonth() + 1).padStart(2, "0");
        params.month = `${y}-${m}`;
      }
      if (filterCategory !== "all") params.category = filterCategory;

      const svc = new FinanceService();
      const res = await svc.getExpenses(params);
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
      setExpenses(data);
    } catch {
      enqueueSnackbar("Failed to load expenses", { variant: "error" });
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setDeletingId(id);
      await new FinanceService().deleteExpense(String(id));
      enqueueSnackbar("Expense deleted");
      loadExpenses();
    } catch {
      enqueueSnackbar("Failed to delete expense", { variant: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, [filterMonth, filterCategory, selectedPeriod?.dateFrom, selectedPeriod?.dateTo]);

  // ── Totals ─────────────────────────────────────────────────────────────────
  const total = expenses.reduce((s, e) => s + e.amount, 0);
  const byCategory = EXPENSE_CATEGORIES.map((cat) => ({
    ...cat,
    total: expenses
      .filter((e) => e.category === cat.value)
      .reduce((s, e) => s + e.amount, 0),
  })).filter((c) => c.total > 0);

  return (
    <Stack spacing={2.5}>
      {/* ── Header actions ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={500}>
          Expenses
        </Typography>
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => setAddOpen(true)}>
          Record Expense
        </Button>
      </Stack>

      {/* ── Info alert ── */}
      <Alert severity="info" sx={{ borderRadius: 1 }}>
        Record non-sales expenses here — rent, salaries, repairs, utilities,
        housekeeping. These feed into your P&amp;L statement and Trial Balance.
      </Alert>

      {/* ── Summary tiles ── */}
      {byCategory.length > 0 && (
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 2,
                bgcolor: "error.lighter",
                border: "1px solid",
                borderColor: "error.light",
              }}>
              <Typography variant="caption" color="error.dark">
                Total expenses
              </Typography>
              <Typography
                variant="h5"
                fontFamily="monospace"
                color="error.dark"
                sx={{ mt: 0.5 }}>
                {fCurrency(total)}
              </Typography>
              <Typography variant="caption" color="error.main">
                {expenses.length} entries
              </Typography>
            </Card>
          </Grid>
          {byCategory.slice(0, 3).map((cat) => (
            <Grid item xs={12} sm={6} md={3} key={cat.value}>
              <Card sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Iconify
                    icon={cat.icon}
                    width={16}
                    sx={{ color: "text.secondary" }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {cat.label}
                  </Typography>
                </Stack>
                <Typography variant="h6" fontFamily="monospace">
                  {fCurrency(cat.total)}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Filters ── */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          select
          size="small"
          label="Category"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          sx={{ minWidth: 200 }}>
          <MenuItem value="all">All Categories</MenuItem>
          {EXPENSE_CATEGORIES.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Month"
            views={["year", "month"]}
            value={filterMonth}
            onChange={(v) => setFilterMonth(v)}
            slotProps={{ textField: { size: "small", sx: { minWidth: 140 } } }}
          />
        </LocalizationProvider>

        {(filterMonth || filterCategory !== "all") && (
          <Button
            size="small"
            color="error"
            onClick={() => {
              setFilterMonth(null);
              setFilterCategory("all");
            }}
            startIcon={<Iconify icon="eva:close-fill" />}>
            Reset
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {expenses.length} expenses · {fCurrency(total)} total
        </Typography>
      </Stack>

      {/* ── Table ── */}
      <Card>
        <Scrollbar>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">VAT</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Iconify
                      icon="eva:inbox-fill"
                      width={36}
                      sx={{
                        color: "text.disabled",
                        display: "block",
                        mx: "auto",
                        mb: 1,
                      }}
                    />
                    <Typography variant="body2" color="text.disabled">
                      No expenses recorded
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Click "Record Expense" to add your first entry
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((exp) => {
                  const cat = EXPENSE_CATEGORIES.find(
                    (c) => c.value === exp.category,
                  );
                  return (
                    <TableRow key={exp.id} hover>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {fDate(exp.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {exp.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {cat && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}>
                            <Iconify
                              icon={cat.icon}
                              width={14}
                              sx={{ color: "text.secondary" }}
                            />
                            <Typography variant="caption">
                              {cat.label}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          fontWeight={500}
                          color="error.dark">
                          −{fCurrency(exp.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {exp.vatCode && (
                          <Typography
                            variant="caption"
                            sx={{
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.75,
                              bgcolor:
                                exp.vatCode === "S"
                                  ? "#dbeafe"
                                  : exp.vatCode === "E"
                                    ? "#dcfce7"
                                    : exp.vatCode === "Z"
                                      ? "#f3f4f6"
                                      : "#fef9c3",
                              color:
                                exp.vatCode === "S"
                                  ? "#1d4ed8"
                                  : exp.vatCode === "E"
                                    ? "#15803d"
                                    : exp.vatCode === "Z"
                                      ? "#374151"
                                      : "#854d0e",
                              fontWeight: 700,
                              fontFamily: "monospace",
                            }}>
                            {exp.vatCode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {exp.reference ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete expense">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deletingId === exp.id}
                            onClick={() => handleDelete(exp.id)}>
                            {deletingId === exp.id ? (
                              <CircularProgress size={12} />
                            ) : (
                              <Iconify icon="eva:trash-2-fill" width={15} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Scrollbar>
      </Card>

      <AddExpenseDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={loadExpenses}
      />
    </Stack>
  );
}
